import { useState } from 'react'
import {
  choiceBounds, findCountry, isRestaurantQuestion, questionType, scaleStops, searchCountries,
  type TemplateQuestion
} from '@romain130492/topchopsticks'
import { useI18n } from '../../hooks/useI18n'
import RestaurantFinder from './RestaurantFinder'
import MonthYearPicker from './MonthYearPicker'

/**
 * One question, rendered. The web half of
 * wechat/src/components/onboarding/QuestionFlow.jsx.
 *
 * Everything about WHICH control to show comes from `questionType()` in the
 * shared package, never from a local string comparison. The whole class of bug
 * this replaces was a single wrong property name — the web read
 * `question.question_type` where the API sends `type` — which silently made
 * every question a single-select.
 *
 * `onCommit` exists separately from `onChange` because single-select and
 * country auto-advance on tap in the mini program, and copying that is the
 * point. Multi-select, dates, scales and the restaurant finder must not: they
 * need a Continue press, or you could never pick a second option.
 */
export default function QuestionControl({
  question, value, onChange, onCommit
}: {
  question: TemplateQuestion
  value: unknown
  onChange: (value: unknown) => void
  onCommit: (value: unknown) => void
}) {
  const { L, lang } = useI18n()
  const [countryQuery, setCountryQuery] = useState('')
  const type = questionType(question)
  const options = question.options || []

  // ---- restaurant ----------------------------------------------------------
  if (isRestaurantQuestion(question)) {
    return <RestaurantFinder value={value} onChange={onChange} />
  }

  // ---- choices -------------------------------------------------------------
  if (type === 'single_select' || type === 'multi_select') {
    const multi = type === 'multi_select'
    const { max } = choiceBounds(question)
    const selected = Array.isArray(value) ? (value as string[]) : []

    const toggle = (optionValue: string) => {
      const next = selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue]
      // Refuse rather than silently drop the earliest pick — the member chose
      // it on purpose and a vanishing selection reads as a bug.
      if (max && next.length > max) return
      onChange(next)
    }

    return (
      // Multi-select is a wrapping pill grid in the mini program, single-select
      // a stack of full-width cards. Same shapes here, so the two apps look
      // like one product.
      <div className={multi ? 'flex flex-wrap gap-2' : 'space-y-2'}>
        {options.map((option) => {
          const active = multi ? selected.includes(option.value) : value === option.value
          const label = `${option.emoji ? `${option.emoji} ` : ''}${L(option.label as never)}`
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => (multi ? toggle(option.value) : onCommit(option.value))}
              className={
                multi
                  ? `rounded-pill border px-4 py-2.5 text-13 ${
                      active ? 'border-gold bg-gold-faint text-ivory' : 'border-gold-dim bg-card text-muted'
                    }`
                  : `w-full rounded-md border px-4 py-3.5 text-left text-md ${
                      active ? 'border-gold bg-gold-faint text-ivory' : 'border-gold-dim bg-card text-muted'
                    }`
              }
            >
              {label}
            </button>
          )
        })}
      </div>
    )
  }

  // ---- date of birth -------------------------------------------------------
  if (type === 'month_year' || type === 'date') {
    const isBirthday = question.key === 'date_of_birth'

    // One field, one sheet, two wheels — see MonthYearPicker for why neither
    // <input type="month"> nor a pair of <select>s was good enough.
    return (
      <MonthYearPicker
        value={value}
        onChange={onChange}
        minYear={isBirthday ? 1920 : 1900}
        // No one is born in the future.
        maxYear={new Date().getFullYear()}
      />
    )
  }

  // ---- country -------------------------------------------------------------
  if (type === 'country_region') {
    const code = typeof value === 'string' ? value : (value as { country?: string })?.country
    const chosen = findCountry(code)
    if (chosen) {
      return (
        <button
          type="button"
          onClick={() => onChange({ country: '' })}
          className="flex w-full items-center justify-between rounded-sm border border-gold bg-gold-faint px-3 py-3 text-left text-md text-ivory"
        >
          <span>{chosen.flag} {lang === 'zh' ? chosen.zh : chosen.en}</span>
          <span className="text-gold">×</span>
        </button>
      )
    }
    const matches = searchCountries(countryQuery)
    return (
      <div>
        <input
          value={countryQuery}
          onChange={(e) => setCountryQuery(e.target.value)}
          placeholder={lang === 'zh' ? '搜索国家 / 地区' : 'Search your country'}
          className="h-12 w-full rounded-sm border border-gold-dim bg-card px-3 text-md text-ivory placeholder:text-muted-2 focus:border-gold focus:outline-none"
        />
        <div className="mt-2 space-y-1">
          {matches.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { setCountryQuery(''); onCommit({ country: c.code }) }}
              className="w-full rounded-sm border border-gold-dim bg-card px-3 py-2.5 text-left text-13 text-muted"
            >
              {c.flag} {lang === 'zh' ? c.zh : c.en}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ---- scale ---------------------------------------------------------------
  if (type === 'scale') {
    const stops = scaleStops(question).length ? scaleStops(question) : ['introverted', 'ambiverted', 'extroverted']
    const currentIndex = Math.max(0, stops.indexOf(String(value || stops[1] || stops[0])))
    const stopOption = options.find((o) => o.value === stops[currentIndex])
    return (
      <div className="text-center">
        {/* Emoji comes from the option row, editable in the backoffice Question
            Studio — no hardcoded fallback, so what you set is what renders. */}
        <div className="text-hero leading-none">{stopOption?.emoji || ''}</div>
        <p className="mt-2 text-md text-ivory">{L(stopOption?.label as never) || stops[currentIndex]}</p>
        <input
          type="range"
          min={0}
          max={stops.length - 1}
          step={1}
          value={currentIndex}
          onChange={(e) => onChange(stops[Number(e.target.value)])}
          className="mt-4 w-full accent-gold"
        />
      </div>
    )
  }

  // ---- free text -----------------------------------------------------------
  if (type === 'textarea') {
    return (
      <textarea
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={L(question.help_text as never) || ''}
        rows={4}
        className="w-full rounded-sm border border-gold-dim bg-card px-3 py-2.5 text-md text-ivory placeholder:text-muted-2 focus:border-gold focus:outline-none"
      />
    )
  }

  return (
    <input
      value={(value as string) || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={L(question.help_text as never) || (lang === 'zh' ? '请输入' : 'Your answer')}
      inputMode={type === 'number' ? 'numeric' : undefined}
      className="h-12 w-full rounded-sm border border-gold-dim bg-card px-3 text-md text-ivory placeholder:text-muted-2 focus:border-gold focus:outline-none"
    />
  )
}

/** True when tapping an option should move straight on, as the mini program does. */
export function autoAdvances(question: TemplateQuestion | null | undefined): boolean {
  const type = questionType(question)
  return (type === 'single_select' || type === 'country_region') && !isRestaurantQuestion(question)
}

/** Controls tall enough to push the footer button below the fold. */
export function isLongControl(question: TemplateQuestion | null | undefined): boolean {
  return isRestaurantQuestion(question) || questionType(question) === 'country_region'
}
